import { useEffect } from 'react';

import Stack, { type StackProps } from '@liquidweb-dev/undercurrent/Stack';
import toast from '@liquidweb-dev/undercurrent/toast';
import { type LWApiPublicAssetListParamsRawI } from '@liquidweb-dev/club-sauce/public/asset/index.raw';
import { useLeftHandLayout } from 'components/LeftHandLayout';
import useUserMetaState from 'modules/queries/account/user/stateData/useUserMetaState';
import { useInfiniteAssetList } from 'modules/queries/asset/useList';
import { useLatestNotifications } from 'modules/queries/notification/useCurrent';
import useInfiniteScroll from 'utility/hooks/useInfiniteScroll';
import useScheduledCancellationFlag from 'components/CancellationScheduledServerList/useScheduledCancellationFlag';
import { NUTANIX_CATEGORY } from 'utility/nutanixProductCodes';
import CardsWrapper from './CardsWrapper';
import EmptyFiltered from './EmptyFiltered';
import groupServersByParent from './groupServersByParent';
import ListWrapper from './ListWrapper';
import ServerDetails from './ServerDetails';
import ServersListCard from './ServersListCard';
import ServersListNestedGroup from './ServersListNestedGroup';
import ServersListRow from './ServersListRow';
import SkeletonCardItem from './SkeletonCardItem';
import SkeletonListItem from './SkeletonListItem';
import useServerListNesting from './useServerListNesting';
import useServersNestingPreference from './useServersNestingPreference';

export const SERVER_CATEGORIES = [
	'AutoDedicated',
	'StrictDedicated',
	'VirtualDedicated',
	'CloudSitesList',
	'CloudSitesListPlan',
	'ManagedApplication',
	NUTANIX_CATEGORY,
];

export const SERVER_LIST_ALSOWITH: LWApiPublicAssetListParamsRawI['alsowith'] =
	[
		'productDisplayGroup',
		'associationDetails',
		'backupsAndStorage',
		'capabilities',
		'categories',
		'featureDetails',
		'nextInvoiceDate',
		'powerStatus',
		'region',
		'scheduledCancellationDate',
		'template',
		'templateApp',
	];

/** The parent/child links nesting needs; the flat cancellation list doesn't. */
const SERVER_LIST_NESTING_ALSOWITH: NonNullable<
	LWApiPublicAssetListParamsRawI['alsowith']
> = ['privateParent', 'children'];

const SERVER_LIST_REQUEST_ALSOWITH = [
	...(SERVER_LIST_ALSOWITH ?? []),
	...SERVER_LIST_NESTING_ALSOWITH,
];

export interface ServersListProps extends StackProps {
	productDisplayGroup: string;
	project?: string;
	searchValue?: string;
	orderBySort: 'asc' | 'desc';
	onClearFilters: () => void;
}

const ServersList = ({
	productDisplayGroup,
	project,
	searchValue,
	orderBySort,
	onClearFilters,
	...rest
}: ServersListProps) => {
	const { screenSize } = useLeftHandLayout();
	const [viewPreference, , { isLoading: isLoadingUserMeta }] = useUserMetaState<
		'list' | 'card'
	>('serversViewPreference', 'list');
	const isScheduledCancellationFlagEnabled = useScheduledCancellationFlag();

	const {
		data: infiniteAssetList,
		fetchNextPage,
		hasNextPage,
		isFetching,
		isFetchingNextPage,
		isLoading: isLoadingAssetList,
	} = useInfiniteAssetList(
		{
			page_size: 15,
			productDisplayGroup: productDisplayGroup || undefined,
			order_by: { field: 'domain', sort: orderBySort },
			domain_match: searchValue ? 1 : undefined,
			domain: searchValue || undefined,
			category: SERVER_CATEGORIES,
			project_name: project || undefined,
			alsowith: SERVER_LIST_REQUEST_ALSOWITH,
			// With the flag on, exclude servers that already have a cancellation scheduled;
			// CancellationScheduledServerList shows those. With it off, one list holds everything.
			...(isScheduledCancellationFlagEnabled && {
				has_scheduled_termination: false,
			}),
		},
		{ refetchInterval: 30000, keepPreviousData: true },
	);

	useInfiniteScroll({
		isFetching,
		hasNextPage,
		onShouldFetch: () => fetchNextPage(),
	});

	const { data: notifications, isLoading: isLoadingNotifications } =
		useLatestNotifications(
			{},
			{
				refetchInterval: 10000,
			},
		);

	const assetList =
		infiniteAssetList?.pages?.flatMap((page) => page.items) ?? [];

	const [nestingPreference] = useServersNestingPreference();
	// Small screens always use cards, which stay flat
	const isNesting =
		nestingPreference && viewPreference === 'list' && screenSize !== 'small';
	const {
		linkedChildIds,
		extraServers,
		isError: isNestingError,
	} = useServerListNesting(assetList, {
		enabled: isNesting,
		alsowith: SERVER_LIST_REQUEST_ALSOWITH,
	});

	useEffect(() => {
		if (isNesting && isNestingError) {
			toast.danger('Some child servers could not be loaded.');
		}
	}, [isNesting, isNestingError]);

	if (isLoadingUserMeta) {
		return null;
	}

	const isLoadingServers = isLoadingAssetList || isLoadingNotifications;

	if (
		isLoadingServers &&
		(viewPreference === 'card' || screenSize === 'small')
	) {
		return (
			<Stack {...rest}>
				<CardsWrapper>
					<SkeletonCardItem />
					<SkeletonCardItem />
					<SkeletonCardItem />
				</CardsWrapper>
			</Stack>
		);
	}

	if (isLoadingServers && viewPreference === 'list') {
		return (
			<Stack {...rest}>
				<ListWrapper>
					<SkeletonListItem />
					<SkeletonListItem />
					<SkeletonListItem />
				</ListWrapper>
			</Stack>
		);
	}

	if (assetList.length === 0) {
		return (
			<Stack {...rest}>
				<EmptyFiltered
					sx={{ flexGrow: 1, alignSelf: 'center', justifyContent: 'center' }}
					onClearFilters={onClearFilters}
				/>
			</Stack>
		);
	}

	if (viewPreference === 'card' || screenSize === 'small') {
		return (
			<Stack {...rest}>
				<CardsWrapper>
					{assetList.map((assetDetails) => (
						<ServersListCard
							key={assetDetails.uniq_id}
							assetDetails={assetDetails}
							notification={notifications?.[assetDetails.uniq_id]}
						>
							<ServerDetails assetDetails={assetDetails} variant="card" />
						</ServersListCard>
					))}

					{isFetchingNextPage && (
						<>
							<SkeletonCardItem />
							<SkeletonCardItem />
							<SkeletonCardItem />
						</>
					)}
				</CardsWrapper>
			</Stack>
		);
	}

	return (
		<Stack {...rest}>
			<ListWrapper>
				{isNesting
					? groupServersByParent([...assetList, ...extraServers], {
							linkedChildIds,
						}).map((entry) =>
							entry.isParent ? (
								<ServersListNestedGroup
									key={entry.server.uniq_id}
									entry={entry}
									notifications={notifications}
								/>
							) : (
								<ServersListRow
									key={entry.server.uniq_id}
									assetDetails={entry.server}
									notification={notifications?.[entry.server.uniq_id]}
								>
									<ServerDetails assetDetails={entry.server} variant="row" />
								</ServersListRow>
							),
						)
					: assetList.map((assetDetails) => (
							<ServersListRow
								key={assetDetails.uniq_id}
								assetDetails={assetDetails}
								notification={notifications?.[assetDetails.uniq_id]}
							>
								<ServerDetails assetDetails={assetDetails} variant="row" />
							</ServersListRow>
						))}

				{isFetchingNextPage && (
					<>
						<SkeletonListItem />
						<SkeletonListItem />
						<SkeletonListItem />
						<SkeletonListItem />
					</>
				)}
			</ListWrapper>
		</Stack>
	);
};

export default ServersList;
